import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import baseURL from './config';

const INACTIVITY_LIMIT = 10 * 60 * 1000; // 15 minutes

const AdminProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const navigate = useNavigate();
  const inactivityTimerRef = useRef(null);

  // ✅ Logout function
  const handleLogout = useCallback(async () => {
    try {
      await fetch(`${baseURL}/auth/admin/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Admin logout failed:', error);
    }
    setIsAuthenticated(false);
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  // ✅ Auth check
  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${baseURL}/auth/admin/check-auth`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Unauthorized');

      const data = await response.json();
      if (data && data.user && data.user.id) {
        setIsAuthenticated(true);
      } else {
        handleLogout(); // Force logout if user data missing
      }
    } catch (error) {
      console.error('Admin auth check failed:', error);
      setIsAuthenticated(false);
      navigate('/admin/login', { replace: true });
    }
  }, [navigate, handleLogout]);

  // ✅ Inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      handleLogout();
    }, INACTIVITY_LIMIT);
  }, [handleLogout]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (isAuthenticated) {
      window.addEventListener('mousemove', resetInactivityTimer);
      window.addEventListener('keydown', resetInactivityTimer);
      resetInactivityTimer();

      return () => {
        window.removeEventListener('mousemove', resetInactivityTimer);
        window.removeEventListener('keydown', resetInactivityTimer);
        clearTimeout(inactivityTimerRef.current);
      };
    }
  }, [isAuthenticated, resetInactivityTimer]);

  // ✅ Loading state
  if (isAuthenticated === null) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          textAlign: 'center',
        }}
      >
        <CircularProgress color="primary" size={18} />
        <Typography variant="h6" sx={{ mt: 2, color: 'gray', fontSize: '0.8rem' }}>
          Loading Yamba Africa Admin Panel
        </Typography>
      </Box>
    );
  }

  // ✅ Render protected content
  if (isAuthenticated) return children;

  // ✅ Redirect if not authenticated
  return <Navigate to="/admin/login" replace />;
};

export default AdminProtectedRoute;
